import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { TOptions } from "i18next"
import Typography from "@mui/material/Typography"
import Grid from "@mui/material/Grid"
import ControlledInput from "widgets/common/ControlledInput"
import type { SelectOption } from "types/common.types"
import SkeletonWrapper from "./SkeletonWrapper"

interface ConditionalInputProps {
  name: string
  type?: string
  labelKey: string
  labelOptions?: string | TOptions | undefined
  displayedValue: string | number | undefined
  isEditing: boolean
  isLoading: boolean
  orientation?: "horizontal" | "vertical"
  selectOptions?: SelectOption[] | null
}

const ConditionalInput = ({
  name,
  type = "text",
  labelKey,
  labelOptions = labelKey,
  displayedValue,
  isEditing,
  isLoading,
  orientation = "horizontal",
  selectOptions,
}: ConditionalInputProps) => {
  const { t } = useTranslation()
  const options =
    typeof labelOptions === "string" ? { defaultValue: labelOptions } : labelOptions
  const size = useMemo(() => (orientation === "horizontal" ? 6 : 12), [orientation])
  const columnSpacing = useMemo(
    () => (orientation === "horizontal" ? 4 : 0),
    [orientation],
  )
  const rowSpacing = useMemo(() => (orientation === "vertical" ? 2 : 0), [orientation])

  return (
    <Grid container columnSpacing={columnSpacing} rowSpacing={rowSpacing} mb={2}>
      <Grid item xs={size}>
        <Typography>{t(labelKey, options)}</Typography>
      </Grid>
      <Grid item xs={size} mb={isEditing ? -2 : 0}>
        {isEditing ? (
          <ControlledInput
            name={name}
            type={type}
            variant="outlined"
            size="small"
            placeholder={""}
            selectOptions={selectOptions}
          />
        ) : (
          <SkeletonWrapper isWaiting={isLoading} width={150}>
            <Typography
              color={"text.disabled"}
              overflow={"hidden"}
              textOverflow={"ellipsis"}
            >
              {displayedValue === "" ? "-" : displayedValue}
            </Typography>
          </SkeletonWrapper>
        )}
      </Grid>
    </Grid>
  )
}

export default ConditionalInput
